import React from "react";
import "../Assets/css/TermsCondition.css";
import { useNavigate } from "react-router-dom";

const TermsCondition = () => {
  const navigate = useNavigate();

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      <header className="terms-condition">
        <div className="container py-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="logo">BioME.com</div>
            <button
              onClick={goBack}
              className="back-button"
              style={{
                backgroundColor: "transparent",
                border: "none",
                color: "#2d3436",
                fontSize: "1.2rem",
                cursor: "pointer",
              }}
            >
              &larr; Back
            </button>
          </div>
          <p className="tagline">
            🌏 all of you in your very own personal .com with biome! 🌳
          </p>
        </div>
      </header>

      <div className="terms-condition">
        <div className="container">
          <div className="last-updated">Terms last updated: 30 May 2025</div>

          <div className="toc-container">
            <h1>Terms & Conditions</h1>

            <p>
              BioME is a content management and distribution platform that helps
              creators organize and share their digital presence. We believe in
              clarity and simplicity, which is why we've streamlined our terms
              into this straightforward document.
            </p>

            <div className="clause">
              <h2>1. Acceptance of Terms</h2>
              <p>
                Welcome to BioME! These Terms & Conditions, along with our other
                linked policies ("Terms"), govern your use of BioME.com, its
                associated apps, software, and features (collectively, the
                "Platform" or "BioME").
              </p>
              <p>
                When we say "we," "our," or "us," we're referring to BioME Pty
                Ltd and our affiliated companies. By accessing or using BioME,
                you agree to be bound by these Terms. Our support documentation
                also contains important information about using our Platform.
                Please read these Terms carefully. If you disagree with any
                part, you must discontinue your use of BioME.
              </p>
            </div>

            <div className="clause">
              <h2>2. Modifications to the Terms</h2>
              <p>
                BioME is a dynamic platform that we continuously improve.
                Consequently, we may need to update these Terms to reflect
                product evolution, business needs, legal requirements, or to
                protect our legitimate interests. We reserve the right to make
                these changes at any time, and you are responsible for
                periodically reviewing the most current version.
              </p>
              <p>
                If a change is determined by us to have a significant adverse
                impact on you, we will make reasonable efforts to provide at
                least one (1) month's notice. Your continued use of BioME after
                changes are posted constitutes your acceptance of the revised
                Terms. If you do not agree to the changes, you must stop using
                BioME and close your account as outlined in Clause 10.
              </p>
            </div>

            <div className="clause">
              <h2>3. Your Account Responsibilities</h2>
              <p>
                By creating a BioME account, you affirm that you are over 18 and
                legally capable of entering into these Terms. You agree to
                provide accurate account information and, if necessary, verify
                your identity or linked third-party accounts. You are
                responsible for keeping this information current.
              </p>
              <p>
                If you are using BioME on behalf of an organization, you
                represent that you have the authority to bind that entity to
                these Terms. You are solely responsible for maintaining the
                security of your account and for all activities that occur under
                it. Use a strong, unique password and notify us immediately of
                any unauthorized access. You may not transfer your account to
                another person or use it in any unlawful manner, or in a way
                that, in our reasonable judgment, harms BioME, our reputation,
                or infringes on the rights of others. Automated creation of
                accounts is prohibited.
              </p>
            </div>

            <div className="clause">
              <h2>4. Selecting a Username</h2>
              <p>
                Your username is an important part of your identity on BioME,
                but it must be chosen respectfully. It must not infringe on the
                intellectual property ("IP") rights of others (such as
                trademarks or copyrights), impersonate another person or brand,
                or be offensive, misleading, or registered solely for the
                purpose of resale ("Username Squatting").
              </p>
              <p>
                We reserve the right to review your username and, if necessary,
                request that you change it. Failure to comply may result in
                account suspension or termination. If a third party claims your
                username violates their IP rights, they may submit a report, and
                you will have the opportunity to respond. Additionally, if your
                account shows no login activity, content updates, or traffic for
                a continuous 6-month period, we may, at our discretion, reclaim
                the username without notice.
              </p>
            </div>

            <div className="clause">
              <h2>5. Subscription Plans and Cancellation</h2>
              <p>
                BioME offers both free and paid subscription plans. Your plan
                begins when you accept these Terms and continues until you
                cancel it or we terminate it as described in Clause 10. By
                initiating a paid subscription, you authorize us to charge your
                payment method the recurring subscription fee.
              </p>

              <div className="highlight">
                <p>
                  <strong>
                    YOUR SUBSCRIPTION WILL AUTOMATICALLY RENEW, AND YOU
                    AUTHORIZE US TO COLLECT THE APPLICABLE FEES (PLUS TAXES) ON
                    EACH RENEWAL DATE UNLESS YOU CANCEL IT BEFORE YOUR NEXT
                    BILLING CYCLE.
                  </strong>{" "}
                  We recognize that some jurisdictions provide statutory
                  cancellation rights, and this clause does not override them.
                </p>
              </div>

              <p>
                We may modify our subscription plans, including adding,
                removing, or changing features, which may affect the price. We
                will provide reasonable advance notice of any changes that we
                determine to be materially adverse. Continued use after the
                effective date of the change constitutes your acceptance of the
                new plan terms.
              </p>
              <p>
                You can cancel your paid plan at any time through the billing
                settings in your BioME admin panel. Cancellation will typically
                take effect at the end of your current billing period, after
                which your account will revert to a free plan. Payments are
                generally non-refundable. However, if you cancel a new paid
                subscription within 72 hours, we may, at our discretion, issue a
                refund. Contact our feedback form for assistance.
              </p>
              <p>
                If you subscribed through the Apple App Store or Google Play
                Store, you must manage your subscription and cancellations
                through those platforms. BioME cannot process refunds for in-app
                purchases. To permanently delete your account and all associated
                data, you can do so in your account settings. This action is
                irreversible.
              </p>
            </div>

            <div className="clause">
              <h2>6. Content Guidelines and Your Obligations</h2>
              <p>
                We encourage the diverse content our users share. To maintain a
                safe environment for everyone, you must adhere to our Community
                Standards. Violations may lead to content removal or account
                suspension.
              </p>
              <p>
                "Your content" refers to any text, images, videos, links, or
                other materials you add to your BioME profile. You are solely
                responsible for your content and warrant that:
              </p>
              <ul>
                <li>
                  You own the content or have all necessary rights to share it
                  and grant us the license in Clause 7.
                </li>
                <li>
                  It does not violate any third party's privacy, publicity, or
                  IP rights.
                </li>
                <li>
                  It is truthful, not misleading or deceptive, and complies with
                  all applicable laws.
                </li>
                <li>
                  It is free of malicious code, such as viruses or malware.
                </li>
                <li>
                  It does not involve the use of bots, scrapers, or automated
                  systems to collect data from the Platform.
                </li>
                <li>
                  It does not contain unauthorized advertising or commercial
                  solicitations.
                </li>
                <li>It complies with our Community Standards.</li>
              </ul>
              <p>
                We may restrict or remove content that violates the law or these
                Terms, even if it is legal in some jurisdictions, to keep BioME
                safe.
              </p>
            </div>

            <div className="clause">
              <h2>7. License to Your Content</h2>
              <p>
                To operate and promote BioME, we need certain permissions from
                you. By posting content, you grant us a worldwide, royalty-free,
                perpetual license to use, display, distribute, modify, and
                create derivative works of your content, as well as use your
                name, likeness, and voice, on the Platform and in our marketing
                materials.
              </p>
              <p>
                You retain all ownership rights to your content. However, please
                be aware that content on BioME is public and may be viewed,
                shared, and used by others across the internet. Do not post
                sensitive personal information (like government ID numbers) that
                you do not want publicly available. You may only post another
                individual's personal information with their explicit consent.
                We are not obligated to monitor your content but reserve the
                right to remove it or apply content warnings at our discretion.
              </p>
            </div>

            <div className="clause">
              <h2>8. Your Relationship with Your Audience</h2>
              <p>
                You are responsible for the individuals who visit your BioME
                profile ("Profile Visitors") and any customers who purchase from
                you ("End Users"). This responsibility includes:
              </p>
              <ul>
                <li>
                  Managing how End Users interact with your profile and content.
                </li>
                <li>
                  Ensuring that your sales, data collection, and marketing
                  practices comply with all laws applicable to your End Users.
                </li>
                <li>
                  Ensuring transactions with End Users comply with the terms of
                  our third-party payment partners (see Clause 9).
                </li>
              </ul>
              <p>
                You may receive voluntary donations from End Users. These
                donations must be for personal support and not for fundraising
                on behalf of charities or other organizations, unless you are
                authorized to do so.
              </p>
            </div>

            <div className="clause">
              <h2>9. Earning Revenue on BioME (Yet to be Implemented)</h2>
              <p>
                You may gain access to features or programs that allow you to
                earn money ("Monetization Programs"). These include:
              </p>
              <ul>
                <li>
                  <strong>Direct Earnings:</strong> Revenue from selling your
                  own digital products or services (e.g., e-books, courses).
                </li>
                <li>
                  <strong>Partner Earnings:</strong> Commissions from affiliate
                  marketing or other partner programs.
                </li>
              </ul>
              <p>(Collectively, "Earnings").</p>

              <h3>Payment Partners:</h3>
              <ul>
                <li>
                  For <strong>Direct Earnings</strong>, you must set up an
                  account with our designated third-party payment processor
                  ("Payment Processor") and agree to their terms.
                </li>
                <li>
                  For <strong>Partner Earnings</strong>, you must set up an
                  account with our designated payout provider ("Payout
                  Provider") and agree to their terms to receive your Earnings.
                </li>
              </ul>

              <h3>Eligibility:</h3>
              <p>
                To participate in any Monetization Program, you must meet and
                maintain the "Participation Conditions," such as residing in an
                eligible country, having a verified and active account in "Good
                Standing" (meaning no violations of these Terms or the law), and
                complying with any other published criteria. We may revoke your
                access if you fail to meet these conditions.
              </p>
              <p>
                To actually receive <strong>Partner Earnings</strong>, you must
                also meet "Earning Conditions," such as completing identity
                verification (KYC) with the Payout Provider, providing valid
                bank details ("Payment Information"), and meeting minimum payout
                thresholds.
              </p>

              <h3>Payouts:</h3>
              <ul>
                <li>
                  <strong>Direct Earnings</strong> are settled by the Payment
                  Processor per their agreement with you.
                </li>
                <li>
                  <strong>Partner Earnings</strong> are paid out as "Available
                  Funds" (earnings that are final and processed) to your Payout
                  Account, typically on a monthly schedule. Earnings marked as
                  "Pending" are not guaranteed until they convert to Available
                  Funds.{" "}
                  <strong>
                    YOU HAVE NO RIGHT TO PENDING EARNINGS, AND THEY ARE
                    FORFEITED UPON ACCOUNT TERMINATION.
                  </strong>
                </li>
              </ul>

              <h3>Seller of Record:</h3>
              <p>
                For Direct Earnings, you are the "Seller of Record." This means
                you are solely responsible for the sale, including product
                delivery, quality, customer service, refunds, chargebacks, and
                legal compliance. BioME is not liable for your products, sales,
                or interactions with End Users, and you agree to indemnify us
                for any related losses.
              </p>

              <h3>Disclosures:</h3>
              <p>
                You are legally required to disclose any material connections,
                such as affiliate commissions or gifted products, to your End
                Users. You must comply with all applicable disclosure laws
                (e.g., FTC guidelines). If BioME provides tools to facilitate
                these disclosures, you must not remove or obscure them. You are
                liable for any losses we suffer due to your failure to comply.
              </p>
            </div>

            <div className="clause">
              <h2>10. Account Suspension and Termination</h2>
              <p>
                We may suspend or terminate your account, adjust your plan, or
                restrict access to features if you breach these Terms, our
                Community Standards, or other policies. The action taken will be
                proportionate to the violation. We may also take these steps to
                comply with a legal order or to prevent harm.
              </p>
              <p>
                For inactive free accounts (e.g., no login or traffic for 6
                months), we reserve the right to terminate them without notice.
                If your account is terminated for breach, you are not entitled
                to a refund of pre-paid fees, where permitted by law. We are not
                liable for any content lost due to suspension, termination, or
                plan downgrades.
              </p>
              <p>
                If you believe your account was terminated in error, contact us
                at feedback form. Both parties agree to engage in good-faith
                discussions for at least one (1) month to resolve the dispute
                before initiating any legal action.
              </p>
            </div>

            <div className="clause">
              <h2>11. Your Feedback</h2>
              <p>
                We welcome your suggestions and feedback. If you participate in
                beta tests or provide ideas, you grant us an irrevocable,
                royalty-free right to use that feedback for any purpose without
                obligation to you. Beta features are provided "as is" and may be
                less reliable than the core Platform.
              </p>
            </div>

            <div className="clause">
              <h2>12. Our Intellectual Property and Your Use Rights</h2>
              <p>
                We grant you a limited, non-exclusive, non-transferable, and
                revocable license to use BioME for its intended purposes. All
                rights, title, and interest in the Platform and its underlying
                technology ("BioME IP") are and will remain our exclusive
                property. You may not use our name, logos, or IP without our
                prior written consent.
              </p>
              <p>
                You agree not to misuse the Platform, including by using bots,
                scrapers, or other automated means to collect data from user
                profiles or the Platform.
              </p>
              <p>
                As a Profile Visitor, you are granted a limited right to view
                and interact with user profiles. Any purchases made through a
                creator's profile are transactions between you and that creator
                (the Seller). BioME is not a party to these transactions and is
                not liable for the quality, delivery, or support of any products
                or services sold by users.
              </p>
            </div>

            <div className="clause">
              <h2>13. Fees and Payments</h2>
              <p>
                <strong>Subscription Fees:</strong> Paid plans require timely
                payment of recurring fees. Fees are billed in advance on a
                monthly or annual basis. You must provide accurate billing
                information and authorize us to charge your payment method. If
                automatic billing fails, we may issue an invoice, and your
                account may be suspended until payment is received.
              </p>
              <p>
                <strong>BioME Transaction Fees:</strong> A fee may be applied to
                your Earnings from Monetization Programs, as detailed on our
                website. This fee is deducted before your Earnings are paid out.
              </p>
              <p>
                <strong>Third-Party Fees:</strong> Our Payment Processors and
                Payout Providers (e.g., Stripe) charge their own processing
                fees, which are deducted from your Earnings. BioME is not
                responsible for these fees.
              </p>
              <p>
                <strong>Fee Changes:</strong> We may change our fees with
                reasonable notice. If you disagree with a fee change, you may
                cancel your subscription before the next billing cycle.
              </p>
            </div>

            <div className="clause">
              <h2>14. Tax Obligations</h2>
              <p>
                <strong>Taxes on BioME Fees:</strong> Depending on your
                location, subscription fees may be exclusive of taxes (e.g.,
                VAT, GST, sales tax), which will be added to your invoice. You
                are responsible for any taxes associated with your payments to
                us. If you are tax-exempt, you must provide us with a valid
                exemption certificate.
              </p>
              <p>
                <strong>Taxes on Your Earnings:</strong> You are solely
                responsible for reporting and paying any taxes (including income
                tax) on Earnings you generate through the Platform. We may be
                required to withhold taxes from your payouts where applicable.
              </p>
              <p>
                <strong>Tax Reporting:</strong> We may be required to collect
                tax identification information and report your Earnings to tax
                authorities (e.g., via forms like 1099-K). You authorize us to
                do so and consent to electronic delivery of tax documents.
              </p>
            </div>

            <div className="clause">
              <h2>15. Privacy and Data</h2>
              <p>
                Protecting privacy is a core commitment. Our Privacy Notice
                explains how we handle personal 9. Earning Revenue odata. Our
                Data Processing Addendum ("DPA"), incorporated into these Terms,
                outlines our respective obligations regarding the personal data
                of your Profile Visitors. By using BioME, you agree to the DPA
                if it applies to you.
              </p>
              <p>
                Data generated from the use of the Platform ("Data") is owned by
                BioME. We may provide you with analytics based on this Data
                ("Data Analytics"). While we strive for accuracy, we do not
                warrant that the Data Analytics are complete or error-free.
              </p>
            </div>

            <div className="clause">
              <h2>16. Confidentiality</h2>
              <p>
                We may occasionally share confidential information with you
                (e.g., about upcoming features). You must keep this information
                secure and confidential, using reasonable care to prevent
                unauthorized disclosure, unless we explicitly state that certain
                information can be shared.
              </p>
            </div>

            <div className="clause">
              <h2>17. Content Recommendations</h2>
              <p>
                BioME may recommend products or content to you or your audience
                based on data analytics. These are automated suggestions and not
                endorsements by BioME.
              </p>
            </div>

            <div className="clause">
              <h2>18. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, BioME shall not be
                liable for any indirect, incidental, special, consequential, or
                punitive damages, including loss of profits, data, or goodwill,
                arising from your use of the Platform.
              </p>
              <p>
                Your sole remedy against BioME for any dissatisfied service is
                to stop using the Platform. Our total cumulative liability to
                you for any claim will not exceed the greater of (a) the amount
                you paid to BioME in the 12 months preceding the event giving
                rise to the liability, or (b) US $100.
              </p>
            </div>

            <div className="clause">
              <h2>19. Disclaimer of Warranties</h2>
              <p>
                The Platform is provided "AS IS" and "AS AVAILABLE" without
                warranties of any kind, either express or implied. We disclaim
                all warranties, including merchantability, fitness for a
                particular purpose, and non-infringement. We do not warrant that
                the Platform will be uninterrupted, secure, error-free, or free
                of harmful components.
              </p>
              <p>
                This clause applies to the maximum extent permitted by law. For
                jurisdictions that mandate certain warranties (like under the
                Australian Consumer Law), our liability is limited to the
                re-supply of the services or the cost of re-supply.
              </p>
            </div>

            <div className="clause">
              <h2>20. Third-Party Services</h2>
              <p>
                BioME integrates with various third-party services (e.g.,
                payment gateways). Your use of these services is subject to
                their respective terms and conditions. We do not endorse or
                assume any liability for third-party services, and any issues
                with them may affect your access to linked features on BioME.
              </p>
              <p>
                A list of our key subprocessors and third-party terms can be
                found in our Privacy Notice and at the bottom of our website.
              </p>
            </div>
          </div>
        </div>
      </div>
      <footer className="terms-condition">
        <p>&copy; 2026 BioME.com . All rights reserved.</p>
        <p>Need help? Contact us at our feedback form</p>
      </footer>
    </>
  );
};

export default TermsCondition;
